import { FC, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import Imgclose from "assets/images/input-img-close-icon.svg";
import Button from "components/particles/primary-button";
import img from "assets/images/input-img-icon.svg";
import {
  Container,
  Dropdown,
  Option,
  SelectContainer,
  SelectedOption,
  StyledReactModal,
  TextArea,
} from "./style";

import { useForm } from "react-hook-form";
import FormErrorMessage from "components/particles/forms/form-error-message";
import { dispatch } from "store";
import { setModalClosed } from "store/user-slice";
import { useSelector } from "react-redux";

import { IngredientDetailDTO } from "utils/helpers/models/ingredient-detail.dto";
import { useIngredientDetail } from "containers/Ingredients/ingrident-detail/useHook";
import dropdownIcon from "assets/images/dropdown.svg";

interface IngredientModelProps {
  id: number;
  data: any;
  ingredientId: number;
}

const IngredientDetailPopup: FC<IngredientModelProps> = ({
  id,
  data,
  ingredientId,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [dataById, setDatabyId] = useState<any>();
  const [shouldRemoveImage, setShouldRemoveImage] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState({
    name: "",
    email: "",
    gender: "",
    price: "",
    quantity: "",
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useForm<IngredientDetailDTO>();
  const { postIngredientDetail, putIngredientDetail } = useIngredientDetail();
  const { addIngredientDetailModalIsOpen } = useSelector(
    (state: any) => state.user
  );

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      fontSize: "2rem",
      width: "68.5rem",
      borderRadius: "3rem",
    },
  };
  function closeModal() {
    setSelectedImage(null);
    setImageFile(null);
    setShouldRemoveImage(false);
    setImageError("");
    dispatch(setModalClosed("Ingredient Detail"));
  }
  const handleChange = (field: any, value: any) => {
    setSearchQuery({
      ...searchQuery,
      [field]: value,
    });
  };
  const handleImageChange = (event: any) => {
    if (event?.target?.files?.[0]) {
      const file = event.target.files[0];
      setSelectedImage(URL.createObjectURL(file));
      setImageFile(file);
      setShouldRemoveImage(false);
      setImageError(""); // Clear error when image is selected
    }
  };

  function closeImg() {
    setSelectedImage(null);
    setImageFile(null);
    if (id !== 0) {
      setShouldRemoveImage(true);
    }
    // Don't set error here, let validation handle it on submit
  }

  const onSubmit = async (data: IngredientDetailDTO) => {
    if (!ingredientId) {
      console.error("ingredientId is required.");
      return;
    }

    // Validate image
    setImageError("");
    if (id === 0) {
      // For add: image is required
      if (!imageFile) {
        setImageError("Image is required");
        return;
      }
    } else {
      // For update: image is required (either existing image or new image)
      // If user removed image (shouldRemoveImage = true), they must provide a new one
      if (shouldRemoveImage && !imageFile) {
        setImageError("Image is required");
        return;
      }
      // If no existing image and no new image selected
      if (!selectedImage && !imageFile) {
        setImageError("Image is required");
        return;
      }
    }

    const formData = new FormData();

    if (id === 0) {
      // Add new ingredient detail
      formData.append("ingredientId", ingredientId.toString());
      formData.append("price", data.price.toString());
      formData.append("quantity", data.quantity.toString());
      if (data.unit) {
        formData.append("unit", data.unit);
      }
      if (data.description) {
        formData.append("description", data.description);
      }
      if (imageFile) {
        formData.append("image", imageFile);
      }
      await postIngredientDetail(formData);
    } else {
      // Update existing ingredient detail
      if (data.price !== undefined) {
        formData.append("price", data.price.toString());
      }
      if (data.quantity !== undefined) {
        formData.append("quantity", data.quantity.toString());
      }
      if (data.unit) {
        formData.append("unit", data.unit);
      }
      if (data.description) {
        formData.append("description", data.description);
      }
      if (shouldRemoveImage) {
        formData.append("image", "null");
      } else if (imageFile) {
        formData.append("image", imageFile);
      }
      await putIngredientDetail(id, formData);
    }
  };

  const units = [
    "Bag",
    "Bunch",
    "Clove",
    "Cup",
    "Dash",
    "Dozen",
    "Fluid_ounce",
    "Gallon",
    "Gram",
    "Kilogram",
    "Liter",
    "Ounce",
    "Pound",
    "Piece",
    "Pinch",
    "Slice",
    "Stick",
    "Tablespoon",
    "Teaspoon",
    "Pack",
  ];

  useEffect(() => {
    if (id === 0) {
      setDatabyId(null);
    } else {
      const ingredientsDetails = data?.[0]?.IngredientsDetails;

      if (ingredientsDetails && Array.isArray(ingredientsDetails)) {
        const dataById = ingredientsDetails.find(
          (ingredient: any) => ingredient.id === id
        );

        if (dataById) {
          setDatabyId(dataById);
        } else {
          console.error(`No ingredient found with ID: ${id}`);
          setDatabyId(null);
        }
      } else {
        console.error("IngredientsDetails is not available or not an array.");
        setDatabyId(null);
      }
    }
  }, [id, data]);

  useEffect(() => {
    if (ingredientId) {
      setValue("ingredientId", ingredientId);
    }
  }, [ingredientId]);

  useEffect(() => {
    if (dataById) {
      for (let key in new IngredientDetailDTO()) {
        if (dataById[key] !== undefined) {
          setValue(key as keyof IngredientDetailDTO, dataById[key]);
        }
      }
      if (dataById.unit) {
        setSelectedOption(dataById.unit);
      }
      if (dataById.image) {
        setSelectedImage(dataById.image);
      }
      setImageFile(null);
      setShouldRemoveImage(false);
    }
  }, [dataById, setValue]);

  const [selectedOption, setSelectedOption] =
    useState<string>("Select an option");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleSelect = (option: string) => {
    setSelectedOption(option);
    setIsOpen(false);
    setValue("unit", option);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  return (
    <StyledReactModal
      isOpen={addIngredientDetailModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Example Modal"
    >
      <Container onSubmit={handleSubmit(onSubmit)}>
        <button className="close-button" onClick={closeModal}>
          <img src={close} alt="img" />
        </button>
        <div className="model-container">
          <div className="popup-title">
            <h1>{id ? "Update Ingredient Detail" : "Add Ingredient Detail"}</h1>
          </div>
          <div className="add-ingredient-container">
            {/* Image Upload */}
            <div className="ingredient-image-container">
              {selectedImage && !shouldRemoveImage ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img
                    className="ingredient-image"
                    src={selectedImage}
                    alt="Selected"
                  />
                  <img
                    className="close-icon"
                    src={Imgclose}
                    alt="Remove"
                    onClick={closeImg}
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      cursor: "pointer",
                      width: "24px",
                      height: "24px",
                      zIndex: 10,
                    }}
                  />
                </div>
              ) : (
                <>
                  <label
                    htmlFor="image-input"
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "65px",
                      height: "65px",
                    }}
                  >
                    <img
                      src={img}
                      alt="Upload"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </label>
                  <input
                    id="image-input"
                    type="file"
                    accept="image/*"
                    className="hidden-input"
                    onChange={handleImageChange}
                  />
                </>
              )}
              {imageFile && <p style={{ fontSize: "1.2rem", marginTop: "0.5rem" }}>{imageFile.name}</p>}
              {imageError && (
                <p style={{ color: "red", fontSize: "1.2rem", marginTop: "0.5rem" }}>
                  {imageError}
                </p>
              )}
            </div>

            {/* Amount (with $ sign) */}
            <div className="amount-input">
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#000",
                    fontSize: "1.6rem",
                  }}
                >
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  style={{ paddingLeft: "2.2rem" }}
                  {...register("price", { required: true })}
                />
              </div>
              <FormErrorMessage error={errors.price} />
            </div>

            {/* Quantity and Unit in one row */}
            <div className="quantity-row">
              <div className="quantity-amount">
                <input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Quantity"
                  {...register("quantity", {
                    required: true,
                    validate: value => Number.isInteger(Number(value)) || "Only integers allowed"
                  })}
                />
                <FormErrorMessage error={errors.quantity} />
              </div>

              <div className="quantity-amount">
                <Dropdown {...register("unit", { required: true })}>
                  <option value="" style={{ color: "black" }}>
                    Select Unit
                  </option>
                  {units.map((option: string, index) => (
                    <Option key={index} value={option}>
                      {option}
                    </Option>
                  ))}
                </Dropdown>
                <FormErrorMessage error={errors.unit} />
              </div>
            </div>

            {/* Description */}
            <TextArea
              rows={10}
              cols={50}
              placeholder="Description"
              style={{ color: "black" }}
              {...register("description")}
            />
          </div>
          <div className="add-button">
            <Button
              title={id === 0 ? "Add" : "Update"}
              width="51.2rem"
              backgroundcolor="var(--primary)"
              fill="true"
            />
          </div>
        </div>
      </Container>
    </StyledReactModal>
  );
};

export default IngredientDetailPopup;
